import { FC, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { KTIcon } from '../../../../_metronic/helpers'
import { fetchData, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { SelectOption, StepCommonProps, TargetMasterOption } from '../../types/ads'
import { TargetPreset } from '../../types/adsSettings'

interface Props extends StepCommonProps {
  targetMasters: TargetMasterOption[]
  regionList: SelectOption[]
  interestList: SelectOption[]
  goToNext: () => void
}

const isAllSelected = (values: (string | number)[]): boolean => {
  if (!Array.isArray(values) || values.length === 0) return true
  return values.every((v) => v === 0 || v === '0')
}

const MIN_AGE = 0
const MAX_AGE = 100

// Validates the [from, to] age pair. Returns an error message, or '' when valid.
const validateAgeRange = (values: (string | number)[]): string => {
  const [from, to] = [values?.[0], values?.[1]]
  const isBlank = (v: any) => v === '' || v === null || v === undefined
  if (isBlank(from) || isBlank(to)) return `Enter both "From" and "To" age`

  const fromNum = Number(from)
  const toNum = Number(to)
  if (!Number.isInteger(fromNum) || !Number.isInteger(toNum)) return 'Age must be a whole number'
  if (fromNum < MIN_AGE || toNum < MIN_AGE) return `Age cannot be less than ${MIN_AGE}`
  if (fromNum > MAX_AGE || toNum > MAX_AGE) return `Age cannot be more than ${MAX_AGE}`
  if (fromNum > toNum) return `"From" age cannot be greater than "To" age`
  return ''
}

const TargetingStep: FC<Props> = ({ t, adsId, adminId, vendorId, adData, targetMasters, regionList, interestList, onSaveSuccess, goToNext }) => {

  const safeMasters = Array.isArray(targetMasters) ? targetMasters : []
  const safeRegions = Array.isArray(regionList) ? regionList : []
  const safeInterests = Array.isArray(interestList) ? interestList : []

  const TARGET_PRESETS: Record<string, TargetPreset> = useMemo(() => ({
    age: {
      allLabel: '-',
      choices: [],
      useRange: true,
    },
    gender: {
      allLabel: 'All Genders',
      choices: [
        { value: 'MALE', label: 'Male' },
        { value: 'FEMALE', label: 'Female' },
      ],
    },
    region: {
      allLabel: 'All Regions',
      choices: safeRegions.map((r) => ({ value: r.value, label: r.label })),
      useDropdown: true,
    },
    userlevel: {
      allLabel: 'All Levels',
      choices: [
        { value: 'Basic', label: 'Basic' },
        { value: 'Silver', label: 'Silver' },
        { value: 'Gold', label: 'Gold' },
        { value: 'Platinum', label: 'Platinum' },
      ],
    },
    interest: {
      allLabel: 'All Interests',
      choices: safeInterests.map((i) => ({ value: i.value, label: i.label })),
    },
  }), [safeRegions, safeInterests])

  const [targetState, setTargetState] = useState<Record<number, { values: (string | number)[]; adstargetId: number; isActive: boolean }>>({})
  const [rangeErrors, setRangeErrors] = useState<Record<number, string>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const state: typeof targetState = {}
    safeMasters.forEach((m) => {
      const existing = Array.isArray(adData?.target)
        ? adData.target.find((tg: any) => tg?.key?.toLowerCase() === m.key?.toLowerCase())
        : null

      const lowerKey = m.key?.toLowerCase()
      let defaultValues: (string | number)[] = []
      if (lowerKey === 'age') defaultValues = ['', '']
      else if (lowerKey === 'region') defaultValues = [0]

      let values = Array.isArray(existing?.value) ? existing.value : defaultValues
      // Legacy ads stored [0, 0] for "All Ages" — age is now a required range
      if (lowerKey === 'age' && isAllSelected(values)) values = ['', '']

      state[m.adstargetmasterId] = {
        values,
        adstargetId: existing?.adstargetId ?? 0,
        isActive: true,
      }
    })
    setTargetState(state)
    setRangeErrors({})
  }, [adData, safeMasters.length])

  const getValues = (masterId: number): (string | number)[] => targetState[masterId]?.values ?? []

  const setValues = (masterId: number, values: (string | number)[]) => {
    setTargetState((prev) => ({ ...prev, [masterId]: { ...prev[masterId], values } }))
  }

  const setAll = (master: TargetMasterOption) => {
    const lowerKey = master.key?.toLowerCase()
    if (lowerKey === 'region') setValues(master.adstargetmasterId, [0])
    else setValues(master.adstargetmasterId, [])
  }

  const toggleChoice = (master: TargetMasterOption, choiceValue: string | number) => {
    const current = getValues(master.adstargetmasterId).filter((v) => v !== 0 && v !== '0')
    const exists = current.some((v) => String(v) === String(choiceValue))
    const newValues = exists ? current.filter((v) => String(v) !== String(choiceValue)) : [...current, choiceValue]
    if (newValues.length === 0) setAll(master)
    else setValues(master.adstargetmasterId, newValues)
  }

  const isChoiceActive = (masterId: number, choiceValue: string | number): boolean => {
    const values = getValues(masterId)
    if (isAllSelected(values)) return false
    return values.some((v) => String(v) === String(choiceValue))
  }

  // Age range (from / to) handlers
  const handleRangeChange = (master: TargetMasterOption, index: 0 | 1, raw: string) => {
    // Digits only, never above MAX_AGE (typing "111" lands on "100")
    let digitsOnly = raw.replace(/[^0-9]/g, '').slice(0, 3)
    if (digitsOnly !== '' && Number(digitsOnly) > MAX_AGE) digitsOnly = String(MAX_AGE)
    const current = getValues(master.adstargetmasterId)
    const next: (string | number)[] = [current?.[0] ?? '', current?.[1] ?? '']
    next[index] = digitsOnly
    setValues(master.adstargetmasterId, next as (string | number)[])
    setRangeErrors((prev) => ({ ...prev, [master.adstargetmasterId]: '' }))
  }

  const getRangeValue = (masterId: number, index: 0 | 1): string => {
    const values = getValues(masterId)
    const v = values?.[index]
    return v === null || v === undefined ? '' : String(v)
  }

  // Region dropdown handler
  const handleRegionChange = (master: TargetMasterOption, selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      setAll(master)
    } else {
      const vals = Array.isArray(selected) ? selected.map((s: any) => s.value) : [selected.value]
      setValues(master.adstargetmasterId, vals)
    }
  }

  const getRegionSelectedOptions = (master: TargetMasterOption): SelectOption[] => {
    const values = getValues(master.adstargetmasterId)
    if (isAllSelected(values)) return []
    const lowerKey = master.key?.toLowerCase()
    const preset = TARGET_PRESETS[lowerKey]
    if (!preset) return []
    return values
      .filter((v) => v !== 0 && v !== '0')
      .map((v) => preset.choices.find((c) => String(c.value) === String(v)))
      .filter(Boolean) as SelectOption[]
  }

  // Validates every range based target (age). Returns true when all are valid.
  const validateRanges = (): boolean => {
    const errors: Record<number, string> = {}
    safeMasters.forEach((m) => {
      const preset = TARGET_PRESETS[m.key?.toLowerCase()]
      if (!preset?.useRange) return
      const error = validateAgeRange(getValues(m.adstargetmasterId))
      if (error) errors[m.adstargetmasterId] = error
    })
    setRangeErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Save
  const handleSave = async () => {
    if (!adsId) { toasterrormsg('Please Save Placement First'); return }
    if (!validateRanges()) { toasterrormsg(t('Please Fix The Targeting Errors')); return }
    setSaving(true)
    try {
      const targetPayload = safeMasters.map((m) => {
        const preset = TARGET_PRESETS[m.key?.toLowerCase()]
        let values = targetState[m.adstargetmasterId]?.values ?? []
        // Age is stored as [from, to] — always send numbers
        if (preset?.useRange) values = [Number(values?.[0]), Number(values?.[1])]
        return {
          adstargetId: targetState[m.adstargetmasterId]?.adstargetId ?? 0,
          adstargetmasterId: m.adstargetmasterId,
          isActive: true,
          value: values,
        }
      })

      const completeSteps = { ...(adData?.completeSteps ?? {}), target: true }
      const saveParam = {
        adminId,
        vendorId,
        adsId,
        target: targetPayload,
        completeSteps,
      }

      const response = await fetchData('admin/ads/target/save', saveParam)

      if (String(response?.status) === '200') {
        toastsuccessmsg(response?.message || 'Targeting Saved')
        onSaveSuccess()
        goToNext()
      } else {
        toasterrormsg(response?.message || 'Failed To Save Targeting')
      }
    } catch (error) {
      console.error('Target save error:', error)
      toasterrormsg('Failed To Save Targeting')
    } finally {
      setSaving(false)
    }
  }

  const getSummaryText = (master: TargetMasterOption): string => {
    const values = getValues(master.adstargetmasterId)
    const lowerKey = master.key?.toLowerCase()
    const preset = TARGET_PRESETS[lowerKey]
    if (!preset) return 'All'
    if (preset.useRange) {
      const from = values?.[0]
      const to = values?.[1]
      if (from === '' || to === '' || from == null || to == null) return '-'
      return `${from} - ${to} ${t('years')}`
    }
    if (isAllSelected(values)) return preset.allLabel
    return values
      .filter((v) => v !== 0 && v !== '0')
      .map((v) => preset.choices?.find((c) => String(c.value) === String(v))?.label ?? String(v))
      .join(', ') || preset.allLabel
  }

  const renderSection = (master: TargetMasterOption) => {
    if (!master?.key) return null
    const lowerKey = master.key.toLowerCase()
    const preset = TARGET_PRESETS[lowerKey]
    if (!preset) return null

    const values = getValues(master.adstargetmasterId)
    const allActive = isAllSelected(values)
    const choices = Array.isArray(preset.choices) ? preset.choices : []

    return (
      <div className='adform-target-section' key={master.adstargetmasterId}>
        <div className='fw-bold fs-5 mb-3'>{t(`Target ${master?.label ?? ''}`)}</div>

        {/* Age is a required From / To range */}
        {preset.useRange ? (
          <div className='mb-2'>
            <div className='d-flex flex-wrap align-items-start gap-3'>
              <div className='d-flex align-items-center gap-2'>
                <div>
                  <label className='form-label fs-8 text-gray-600 mb-1'>{t('From')}</label>
                  <input
                    type='text'
                    inputMode='numeric'
                    maxLength={3}
                    className={`form-control form-control-solid w-100px ${rangeErrors[master.adstargetmasterId] ? 'is-invalid' : ''}`}
                    placeholder={String(MIN_AGE)}
                    value={getRangeValue(master.adstargetmasterId, 0)}
                    onChange={(e) => handleRangeChange(master, 0, e.target.value)}
                  />
                </div>
                <span className='text-gray-600 mt-6'>—</span>
                <div>
                  <label className='form-label fs-8 text-gray-600 mb-1'>{t('To')}</label>
                  <input
                    type='text'
                    inputMode='numeric'
                    maxLength={3}
                    className={`form-control form-control-solid w-100px ${rangeErrors[master.adstargetmasterId] ? 'is-invalid' : ''}`}
                    placeholder={String(MAX_AGE)}
                    value={getRangeValue(master.adstargetmasterId, 1)}
                    onChange={(e) => handleRangeChange(master, 1, e.target.value)}
                  />
                </div>
              </div>
            </div>

            {rangeErrors[master.adstargetmasterId] ? (
              <p className='text-danger fs-8 mt-2 mb-0'>{t(rangeErrors[master.adstargetmasterId])}</p>
            ) : (
              <p className='text-muted fs-8 mt-2 mb-0'>
                <KTIcon iconName='information-3' className='fs-7 text-primary me-1' />
                {t(`Both fields are required. Enter an age between ${MIN_AGE} and ${MAX_AGE}.`)}
              </p>
            )}
          </div>
        ) : preset.useDropdown ? (
          <div className='mb-2'>
            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={t('Select Regions...')}
              options={[
                { value: 0, label: t('All Regions') },
                ...choices.map((c) => ({ value: c.value, label: c.label })),
              ]}
              value={allActive
                ? [{ value: 0, label: t('All Regions') }]
                : getRegionSelectedOptions(master)
              }
              onChange={(selected: any) => {
                const selectedArr = Array.isArray(selected) ? selected : []
                const hadAll = allActive
                const nowHasAll = selectedArr.some((s: any) => s.value === 0 || s.value === '0')

                if (nowHasAll && !hadAll) {
                  // User just picked "All Regions" — set to all
                  setAll(master)
                } else if (hadAll && selectedArr.length > 1) {
                  // User had "All" and picked a specific region — remove "All", keep specifics
                  const specifics = selectedArr.filter((s: any) => s.value !== 0 && s.value !== '0')
                  handleRegionChange(master, specifics)
                } else {
                  // Normal selection
                  const specifics = selectedArr.filter((s: any) => s.value !== 0 && s.value !== '0')
                  if (specifics.length === 0) {
                    setAll(master)
                  } else {
                    handleRegionChange(master, specifics)
                  }
                }
              }}
              classNamePrefix='adform-select'
              noOptionsMessage={() => t('No Regions Found')}
            />
            {allActive ? (
              <p className='text-muted fs-8 mt-2 mb-0'>
                <KTIcon iconName='information-3' className='fs-7 text-primary me-1' />
                {t('"All Regions" is selected by default. To target specific regions, select them from the dropdown — "All Regions" will be removed automatically. You cannot remove "All Regions" manually; it stays selected when no specific region is chosen.')}
              </p>
            ) : (
              <p className='text-muted fs-8 mt-2 mb-0'>
                <KTIcon iconName='information-3' className='fs-7 text-primary me-1' />
                {t('To target all regions again, clear your selection or select "All Regions" from the dropdown.')}
              </p>
            )}
          </div>
        ) : (
          <div className='d-flex flex-wrap gap-2'>
            <button
              type='button'
              className={`adform-target-chip ${allActive ? 'adform-target-chip--active' : ''}`}
              onClick={() => setAll(master)}
            >
              {t(preset.allLabel)}
            </button>
            {choices.map((choice) => {
              if (choice?.value == null) return null
              return (
                <button
                  key={String(choice.value)}
                  type='button'
                  className={`adform-target-chip ${isChoiceActive(master.adstargetmasterId, choice.value) ? 'adform-target-chip--active' : ''}`}
                  onClick={() => toggleChoice(master, choice.value)}
                >
                  {t(choice.label)}
                </button>
              )
            })}
          </div>
        )}

        {master?.description && <p className='text-muted fs-8 mt-2 mb-0'>{master.description}</p>}
      </div>
    )
  }

  return (
    <>
      <div className='mb-6'>
        <h4 className='fw-bold fs-4 mb-1'>{t('Targeting Settings')}</h4>
        <p className='text-muted fs-7 mb-0'>{t('Define your target audience for maximum effectiveness')}</p>
      </div>

      <div className='d-flex flex-column gap-5'>
        {safeMasters.map((m) => renderSection(m))}
      </div>

      {safeMasters.length === 0 && <p className='text-muted fs-6'>{t('No targeting options available.')}</p>}

      {safeMasters.length > 0 && (
        <div className='adform-target-summary mt-5'>
          <div className='fw-bold fs-5 mb-3'>{t('Current Targeting Summary')}</div>
          <div className='row g-3'>
            {safeMasters.map((m) => (
              <div className='col-6 col-md-3' key={m.adstargetmasterId}>
                <div className='text-gray-600 fs-7 mb-1'>{m?.label ?? ''}:</div>
                <div className='fw-bold fs-6 text-primary'>{getSummaryText(m)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className='adform-target-note mt-4'>
        <span className='fw-bold'>{t('Note:')}</span>{' '}
        {t('If all options are set to "All", your ad will be shown to all users.')}
      </div>

      <div className='d-flex justify-content-end mt-6'>
        <button
          type='button'
          className='btn btn-primary d-flex align-items-center gap-2 px-5 py-3'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <span className='spinner-border spinner-border-sm' /> : <KTIcon iconName='check' className='fs-3' />}
          <span>{t('Save & Next')}</span>
          <KTIcon iconName='arrow-right' className='fs-3' />
        </button>
      </div>
    </>
  )
}

export { TargetingStep }
